import { NgClass, NgFor } from '@angular/common';
import { ChangeDetectionStrategy, Component, EventEmitter, Input, Output } from '@angular/core';

import { DsTabGroupTheme } from '@design-system/ds-tab-group/types/ds-tab-group-theme.type';

@Component({
  selector: 'ds-tab-group',
  standalone: true,
  imports: [NgFor, NgClass],
  templateUrl: './ds-tab-group.component.html',
  styleUrls: ['./ds-tab-group.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class DsTabGroupComponent {
  /**
   * Visual theme of the tab group.
   *
   * @default 'primary'
   */
  @Input() theme: DsTabGroupTheme = 'primary';

  /**
   * Array of tab labels to display.
   */
  @Input() tabs: string[] = [];

  /**
   * Currently selected tab label.
   * Can be used for two-way binding with `selectedTabChange`.
   */
  @Input() selectedTab: string | null = null;

  /**
   * Event emitted when the selected tab changes.
   * Emits the label of the newly selected tab.
   */
  @Output() selectedTabChange: EventEmitter<string> = new EventEmitter<string>();

  /**
   * Index of the currently selected tab, used to position the tab indicator.
   *
   * Derived from `selectedTab` so that `selectedTab` stays the single source of truth for
   * both the active item and the indicator position. It used to be stored in a field that
   * was only recalculated on init and on click, so any later `selectedTab` change left the
   * indicator parked on the previously selected tab while the active styling moved on.
   *
   * Falls back to `0` while `selectedTab` is unset or is not one of `tabs`.
   */
  get selectedIndex(): number {
    const selectedTabIndex: number = this.tabs.indexOf(this.selectedTab);

    return selectedTabIndex < 0 ? 0 : selectedTabIndex;
  }

  /**
   * Returns CSS class list for the tab group root element.
   */
  get tabGroupClasses(): string[] {
    return ['tab-group', `tab-group--${this.theme}`];
  }

  /**
   * Handles selection of a tab.
   * Emits `selectedTabChange`; the owner of `selectedTab` decides which tab becomes active.
   *
   * @param tab - The label of the tab that was selected.
   */
  onTabSelect(tab: string): void {
    this.selectedTabChange.emit(tab);
  }

  /**
   * `trackBy` for tab label iteration.
   *
   * @param _index - Index in the tabs array (unused).
   * @param tab - Tab label.
   */
  trackByTab(_index: number, tab: string): string {
    return tab;
  }
}
